/* eslint-disable @stencil-community/own-props-must-be-private */
/* STENCIL IMPORTS */
import { Component, Host, h, Prop, Element } from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */
/* CUSTOM IMPORTS */
import { MercuryBundles } from "@genexus/mercury";
import { Locale } from "../../common/locale";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/checkbox",
  "components/combo-box",
  "components/edit",
  "utils/form",
  "utils/layout",
  "utils/typography"
];
@Component({
  tag: "gx-ide-gam-installation-settings",
  styleUrl: "gam-installation-settings.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/gam-installation-settings"]
})
export class GxIdeGamInstallationSettings {
  /**
   * The component hard-coded strings translations.
   */
  #_componentLocale: any;

  @Element() el: HTMLGxIdeGamInstallationSettingsElement;

  #futureUpgradesComboEl!: HTMLChComboBoxRenderElement;
  #panelsCheckboxEl!: HTMLChCheckboxElement;
  #webPanelsCheckboxEl!: HTMLChCheckboxElement;

  /**
   * Callback invoked when the user wants to cancel the exportation process
   */
  @Prop() readonly cancelCallback: CancelCallback;

  /**
   * Determines 'Panels' checkbox activation, enabled only if an SD generator is in the KB.
   * Otherwise, the checkbox is disabled.
   */
  @Prop() readonly canSelectPanels: boolean = false;

  /**
   * Specifies the configuration type to be used
   */
  @Prop() readonly configurationType: "installation" | "update" =
    "installation";

  /**
   * Defines default config for initializing the dialog with stored gam config data
   */
  @Prop() readonly defaultConfiguration: GamConfigData | undefined = {
    importWebPanels: true,
    importPanels: false,
    futureUpgrades: "prompt"
  };

  /**
   * Callback invoked when the user press repair button
   */
  @Prop() readonly repairCallback: RepairCallback;

  /**
   * Callback invoked when the user initiates the export process
   */
  @Prop() readonly saveCallback: SaveCallback;

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
  }

  #cancelCallbackHandler = () => {
    this.cancelCallback();
  };

  #checkboxChangeHandler = () => {
    if (!this.#webPanelsCheckboxEl.value && !this.#panelsCheckboxEl.value) {
      this.#futureUpgradesComboEl.value = "never";
    } else if (this.#futureUpgradesComboEl.value === "never") {
      this.#futureUpgradesComboEl.value = "prompt";
    }
  };

  #repairCallbackHandler = async () => {
    if (this.saveCallback) {
      const data: GamConfigData = {
        importWebPanels: !!this.#webPanelsCheckboxEl.value,
        importPanels: !!this.#panelsCheckboxEl.value,
        futureUpgrades: this.#futureUpgradesComboEl.value
          ? (this.#futureUpgradesComboEl.value as ComboValues)
          : null
      };
      await this.repairCallback(data);
    }
  };

  #renderFilters = () => {
    return (
      <div
        class="field-group"
        role="region"
        aria-label={this.#_componentLocale.filters.filtersHeading}
      >
        <h5 class="subtitle-regular-s margin-block-end-s">
          {this.#_componentLocale.filters.filtersHeading}
        </h5>

        <div class="filters__wrapper">
          <div
            class="field-group"
            role="group"
            aria-labelledby="frontend-objects-label"
          >
            <div class="field field-block">
              <label id="frontend-objects-label" class="label">
                {this.#_componentLocale.filters.frontEndObjects}
              </label>

              <div class="field-group-inline">
                <ch-checkbox
                  class="checkbox"
                  checkedValue="webPanels"
                  value={
                    this.defaultConfiguration?.importWebPanels
                      ? "webPanels"
                      : undefined
                  }
                  caption={this.#_componentLocale.filters.webPanelsCheckbox}
                  onInput={this.#checkboxChangeHandler}
                  ref={(el: HTMLChCheckboxElement) =>
                    (this.#webPanelsCheckboxEl = el as HTMLChCheckboxElement)
                  }
                  part="web-panels-checkbox"
                ></ch-checkbox>

                <ch-checkbox
                  class="checkbox"
                  checkedValue="panels"
                  value={
                    this.defaultConfiguration.importPanels &&
                    this.canSelectPanels
                      ? "panels"
                      : undefined
                  }
                  caption={this.#_componentLocale.filters.panelsCheckbox}
                  disabled={!this.canSelectPanels}
                  onInput={this.#checkboxChangeHandler}
                  ref={(el: HTMLChCheckboxElement) =>
                    (this.#panelsCheckboxEl = el as HTMLChCheckboxElement)
                  }
                  part="panels-checkbox"
                ></ch-checkbox>
              </div>
            </div>

            <div class="field field-block">
              <label class="label" htmlFor="future-upgrades-combo">
                {this.#_componentLocale.filters.futureUpgradesComboLabel}
              </label>
              <ch-combo-box-render
                id="future-upgrades-combo"
                class="combo-box"
                value={this.defaultConfiguration.futureUpgrades}
                ref={(el: HTMLChComboBoxRenderElement) =>
                  (this.#futureUpgradesComboEl =
                    el as HTMLChComboBoxRenderElement)
                }
                model={[
                  {
                    value: "automatic",
                    caption:
                      this.#_componentLocale.manageFutureUpgrades
                        .installAutomatically
                  },
                  {
                    value: "prompt",
                    caption:
                      this.#_componentLocale.manageFutureUpgrades.promptMe
                  },
                  {
                    value: "never",
                    caption:
                      this.#_componentLocale.manageFutureUpgrades.neverUpdate
                  }
                ]}
                part="future-upgrades-combo"
              ></ch-combo-box-render>
            </div>
          </div>
        </div>
      </div>
    );
  };

  #renderDescription = () => {
    return (
      <div class="description">
        {this.configurationType === "installation"
          ? [
              <p class="text-body">
                {
                  this.#_componentLocale.header.headerTextInstallation
                    .firstSentence
                }
              </p>,
              <p class="text-body">
                {
                  this.#_componentLocale.header.headerTextInstallation
                    .secondSentence
                }
              </p>,
              <p class="text-body">
                {
                  this.#_componentLocale.header.headerTextInstallation
                    .thirdSentence
                }
              </p>
            ]
          : [
              <p class="text-body">
                {this.#_componentLocale.header.headerTextUpdate.firstSentence}
              </p>,
              <p class="text-body">
                {this.#_componentLocale.header.headerTextUpdate.secondSentence}
              </p>
            ]}
        {/* header text */}
      </div>
    );
  };

  #saveCallbackHandler = async () => {
    if (this.saveCallback) {
      const data: GamConfigData = {
        importWebPanels: !!this.#webPanelsCheckboxEl.value,
        importPanels: !!this.#panelsCheckboxEl.value,
        futureUpgrades: this.#futureUpgradesComboEl.value
          ? (this.#futureUpgradesComboEl.value as ComboValues)
          : null
      };
      await this.saveCallback(data);
    }
  };

  render() {
    return (
      <Host class="widget spacing-body">
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <div class="dialog-header dialog-header-with-border">
          {this.#renderDescription()}
        </div>
        <div class="main-content field-group">{this.#renderFilters()}</div>

        <div class="footer">
          {this.configurationType === "update" && (
            <div class="footer-info">
              <p>Use Repair to import GAM components again</p>
            </div>
          )}

          <div class="dialog-footer-with-border dialog-footer-space-between">
            {this.configurationType === "update" && (
              <button
                id="repair-button"
                class="button-secondary"
                onClick={this.#repairCallbackHandler}
                part="button button--repair"
              >
                Repair
              </button>
            )}
            <div class="buttons-spacer">
              <button
                id="cancel-button"
                class="button-secondary"
                onClick={this.#cancelCallbackHandler}
                part="button button--cancel"
              >
                Cancel
              </button>
              <button
                id="confirm-button"
                class="button-primary"
                onClick={this.#saveCallbackHandler}
                part="button button--confirm"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}

export type CancelCallback = () => Promise<void>;
type ComboValues = "automatic" | "prompt" | "never";
export type GamConfigData = {
  importWebPanels: boolean;
  importPanels: boolean;
  futureUpgrades: "never" | "prompt" | "automatic";
};

export type RepairCallback = (config: GamConfigData) => Promise<void>;
export type SaveCallback = (data: GamConfigData) => Promise<void>;
